import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import CtaSection from '@/components/base/CtaSection';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === slug),
    [slug]
  );

  const totalPages = Math.ceil(filteredArticles.length / perPage);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [slug]);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 暗网吃瓜 | 实时爆料不间断更新`;
    }
  }, [category]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-foreground-600 text-5xl w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
            <h2 className="text-foreground-300 text-xl mb-2">分类不存在</h2>
            <p className="text-foreground-500 text-sm">请检查分类链接是否正确</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 暗网吃瓜`,
    description: category.description,
    url: `https://anwangchigua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '暗网吃瓜',
      url: 'https://anwangchigua.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://anwangchigua.com' },
      { '@type': 'ListItem', position: 2, name: category.name },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category.name }]} />

        <main>
          {/* Category Header */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
                <div>
                  <h1 className="font-heading text-3xl md:text-4xl text-primary-400 tracking-wider mb-2">
                    {category.name}
                  </h1>
                  <p className="text-foreground-400 text-sm">{category.description}</p>
                </div>
                <span className="text-foreground-600 text-sm whitespace-nowrap">
                  共 {filteredArticles.length} 篇爆料
                </span>
              </div>

              {/* Articles Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {paginatedArticles.map((article, index) => (
                  <div
                    key={article.id}
                    className={index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}
                  >
                    <ArticleCard
                      article={article}
                      variant={index === 0 ? 'large' : 'default'}
                    />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300/40 text-foreground-400 hover:text-foreground-100 hover:border-foreground-400/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-all cursor-pointer ${
                        page === currentPage
                          ? 'bg-primary-500 text-background-950 font-medium'
                          : 'text-foreground-400 hover:text-foreground-100 border border-background-300/40'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300/40 text-foreground-400 hover:text-foreground-100 hover:border-foreground-400/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* CTA */}
          <div className="bg-background-100">
            <CtaSection
              title="还有更多猛料等你发现"
              subtitle="下载暗网吃瓜app，第一时间接收劲爆推送"
              buttonText="下载暗网吃瓜APP"
            />
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}